import React from "react";
import { Trash2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { formatDateOnly } from "@/lib/date";
import { EmptyState } from "@/components/PageHeader";
import { KIND_CHIP, KIND_LABEL, STATUS, noteTitle } from "@/components/shownotes/meta";

function NoteRow({ n, onOpen, onDelete, showStatus, delay }) {
  const s = STATUS[n.status];
  return (
    <Card onClick={() => onOpen(n)} style={{ animationDelay: `${delay}ms` }}
      className="flex cursor-pointer flex-wrap items-center justify-between gap-3 border-slate-200 p-4 shadow-sm transition-[box-shadow,transform] hover:-translate-y-0.5 hover:shadow-md animate-fade-in-up"
      data-testid={`note-row-${n.id}`}>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`rounded-full border px-2 py-0.5 text-[11px] font-bold ${KIND_CHIP[n.kind]}`}>{KIND_LABEL[n.kind]}</span>
          <p className="font-semibold text-slate-900">{noteTitle(n)}</p>
        </div>
        <p className="mt-1 text-xs text-slate-500">
          {formatDateOnly(n.fields?.date)} · by {n.author_name}
          {n.status === "published" && n.recipient_count != null && <> · shared with {n.recipient_count}</>}
          {n.status === "returned" && n.return_reason && <span className="text-red-600"> · {n.return_reason}</span>}
        </p>
      </div>
      <div className="flex items-center gap-2">
        {showStatus && <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ${s.cls}`} data-testid={`note-status-${n.id}`}>{s.label}</span>}
        {onDelete && (
          <button onClick={(e) => { e.stopPropagation(); onDelete(n); }} className="rounded-full p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600" data-testid={`note-delete-${n.id}`} aria-label="Delete">
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>
    </Card>
  );
}

// List of note rows; `canDelete(n)` decides which rows get a delete button.
export default function NotesList({ items, onOpen, onDelete, canDelete = () => false, showStatus, empty, testId }) {
  if (!items.length) return <EmptyState title={empty[0]} description={empty[1]} testId={`${testId}-empty`} />;
  return (
    <div className="space-y-3" data-testid={testId}>
      {items.map((n, i) => (
        <NoteRow key={n.id} n={n} onOpen={onOpen} showStatus={showStatus} delay={Math.min(i, 10) * 30}
          onDelete={canDelete(n) ? onDelete : null} />
      ))}
    </div>
  );
}
